import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FormLabel, FormDescription } from "@/components/ui/form";
import { Tag, Plus, X } from "lucide-react";

interface TagManagerProps {
  tags: string[];
  setTags: (tags: string[]) => void;
  disabled?: boolean;
}

export const TagManager: React.FC<TagManagerProps> = ({ tags, setTags, disabled }) => {
  const [newTag, setNewTag] = useState("");

  const handleAddTag = useCallback(() => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  }, [newTag, tags, setTags]);

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      setTags(tags.filter((tag) => tag !== tagToRemove));
    },
    [tags, setTags]
  );

  return (
    <div className="space-y-3">
      <FormLabel>Tags (Optional)</FormLabel>

      <div className="flex gap-2">
        <Input
          placeholder="Add a tag..."
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddTag();
            }
          }}
          disabled={disabled}
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddTag}
          disabled={!newTag.trim() || disabled}
          title="Add"
        >
          <Plus size={16} />
        </Button>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="flex items-center gap-1 px-2 py-1">
              <Tag size={12} />
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                disabled={disabled}
                className="ml-1 hover:text-red-600"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <FormDescription>Add tags to categorize and organize</FormDescription>
    </div>
  );
};
